import { offlineDatabase, OfflineQueueItem, PendingMutation } from './offlineDatabase'
import { timeEntryService, CreateTimeEntryDto, UpdateTimeEntryDto } from './timeEntryService'
import { timerService } from './timerService'

export interface SyncResult {
  success: boolean
  synced: number
  failed: number
  errors: Array<{ id: number; error: string }>
}

class OfflineQueueService {
  private isProcessing = false
  private readonly maxRetries = 3
  private readonly listeners: Set<(isProcessing: boolean) => void> = new Set()

  // Subscribe to processing state changes
  onProcessingChange(callback: (isProcessing: boolean) => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  private setProcessing(processing: boolean): void {
    this.isProcessing = processing
    this.listeners.forEach(cb => cb(processing))
  }

  // Queue timer actions
  async queueTimerStart(projectId: string, taskId: string | null, description: string): Promise<number> {
    return await offlineDatabase.addToQueue({
      type: 'timer:start',
      payload: { projectId, taskId, description, startedAt: Date.now() },
    })
  }

  async queueTimerPause(): Promise<number> {
    return await offlineDatabase.addToQueue({
      type: 'timer:pause',
      payload: { pausedAt: Date.now() },
    })
  }

  async queueTimerResume(): Promise<number> {
    return await offlineDatabase.addToQueue({
      type: 'timer:resume',
      payload: { resumedAt: Date.now() },
    })
  }

  async queueTimerStop(): Promise<number> {
    return await offlineDatabase.addToQueue({
      type: 'timer:stop',
      payload: { stoppedAt: Date.now() },
    })
  }

  async queueTimerCancel(): Promise<number> {
    return await offlineDatabase.addToQueue({
      type: 'timer:cancel',
      payload: { cancelledAt: Date.now() },
    })
  }

  // Queue entry mutations
  async queueEntryCreate(workspaceId: string, entry: CreateTimeEntryDto): Promise<number> {
    // Save to offline time entries first
    const localId = await offlineDatabase.saveTimeEntry({
      description: entry.description,
      projectId: entry.projectId,
      taskId: entry.taskId,
      startTime: entry.startTime,
      endTime: entry.endTime,
      durationSeconds: Math.floor(
        (new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 1000
      ),
      isBillable: entry.isBillable ?? false,
      tags: entry.tagIds || [],
      status: 'draft',
    })

    // Queue for sync
    await offlineDatabase.addToQueue({
      type: 'entry:create',
      payload: { localId, workspaceId, ...entry },
    })

    return localId
  }

  async queueEntryUpdate(workspaceId: string, id: string, updates: UpdateTimeEntryDto): Promise<number> {
    return await offlineDatabase.addToQueue({
      type: 'entry:update',
      payload: { workspaceId, id, updates, updatedAt: Date.now() },
    })
  }

  async queueEntryDelete(workspaceId: string, id: string): Promise<number> {
    return await offlineDatabase.addToQueue({
      type: 'entry:delete',
      payload: { workspaceId, id, deletedAt: Date.now() },
    })
  }

  // Process all pending queue items
  async processQueue(): Promise<SyncResult> {
    if (this.isProcessing) {
      return { success: true, synced: 0, failed: 0, errors: [] }
    }

    this.setProcessing(true)
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    }

    try {
      const pendingItems = await offlineDatabase.getPendingQueueItems()

      for (const item of pendingItems) {
        if (item.retryCount >= this.maxRetries) {
          result.failed++
          result.errors.push({ id: item.id!, error: 'Max retry attempts exceeded' })
          await offlineDatabase.markQueueItemFailed(item.id!, 'Max retry attempts exceeded')
          continue
        }

        await offlineDatabase.markQueueItemProcessing(item.id!)

        try {
          await this.processQueueItem(item)
          await offlineDatabase.markQueueItemCompleted(item.id!)
          result.synced++
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          await offlineDatabase.markQueueItemFailed(item.id!, errorMessage)
          result.failed++
          result.errors.push({ id: item.id!, error: errorMessage })
        }
      }

      // Clear completed items
      await offlineDatabase.clearCompletedQueueItems()

      result.success = result.failed === 0
      return result
    } finally {
      this.setProcessing(false)
    }
  }

  private async processQueueItem(item: OfflineQueueItem): Promise<void> {
    switch (item.type) {
      case 'timer:start': {
        const { projectId, taskId, description } = item.payload as {
          projectId: string
          taskId: string | null
          description: string
        }
        await timerService.startTimer({ projectId, taskId: taskId || undefined, description })
        break
      }

      case 'timer:pause': {
        await timerService.pauseTimer()
        break
      }

      case 'timer:resume': {
        await timerService.resumeTimer()
        break
      }

      case 'timer:stop': {
        await timerService.stopTimer()
        break
      }

      case 'timer:cancel': {
        await timerService.cancelTimer()
        break
      }

      case 'entry:create': {
        const payload = item.payload as unknown as { localId: number; workspaceId: string } & CreateTimeEntryDto
        const { localId, workspaceId, ...entryData } = payload
        const created = await timeEntryService.createTimeEntry(workspaceId, entryData)
        // Mark as synced with server ID
        await offlineDatabase.markTimeEntrySynced(localId, created.id)
        break
      }

      case 'entry:update': {
        const { workspaceId, id, updates } = item.payload as { workspaceId: string; id: string; updates: UpdateTimeEntryDto }
        await timeEntryService.updateTimeEntry(workspaceId, id, updates)
        break
      }

      case 'entry:delete': {
        const { workspaceId, id } = item.payload as { workspaceId: string; id: string }
        await timeEntryService.deleteTimeEntry(workspaceId, id)
        break
      }

      default:
        throw new Error(`Unknown queue item type: ${item.type}`)
    }
  }

  // Sync pending mutations (legacy support)
  async syncPendingMutations(): Promise<SyncResult> {
    if (this.isProcessing) {
      return { success: true, synced: 0, failed: 0, errors: [] }
    }

    this.setProcessing(true)
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    }

    try {
      const mutations = await offlineDatabase.getUnsyncedMutations()

      for (const mutation of mutations) {
        if (mutation.retryCount >= this.maxRetries) {
          result.failed++
          result.errors.push({ id: mutation.id!, error: 'Max retry attempts exceeded' })
          continue
        }

        try {
          await this.processMutation(mutation)
          await offlineDatabase.markMutationSynced(mutation.id!)
          result.synced++
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          result.failed++
          result.errors.push({ id: mutation.id!, error: errorMessage })
        }
      }

      result.success = result.failed === 0
      return result
    } finally {
      this.setProcessing(false)
    }
  }

  private async processMutation(mutation: PendingMutation): Promise<void> {
    if (mutation.entityType === 'timeEntry') {
      const workspaceId = mutation.data.workspaceId as string
      if (mutation.operation === 'create') {
        const data = mutation.data as unknown as CreateTimeEntryDto
        await timeEntryService.createTimeEntry(workspaceId, data)
      } else if (mutation.operation === 'update') {
        const updates = mutation.data as unknown as UpdateTimeEntryDto
        await timeEntryService.updateTimeEntry(workspaceId, mutation.entityId, updates)
      } else if (mutation.operation === 'delete') {
        await timeEntryService.deleteTimeEntry(workspaceId, mutation.entityId)
      }
    } else {
      throw new Error(`Unsupported entity type: ${mutation.entityType}`)
    }
  }

  // Get queue statistics
  async getQueueStats(): Promise<{
    pending: number
    processing: number
    failed: number
    completed: number
  }> {
    const allItems = await offlineDatabase.queue.toArray()
    
    return {
      pending: allItems.filter(i => i.status === 'pending').length,
      processing: allItems.filter(i => i.status === 'processing').length,
      failed: allItems.filter(i => i.status === 'failed').length,
      completed: allItems.filter(i => i.status === 'completed').length,
    }
  }

  // Retry failed items
  async retryFailed(): Promise<void> {
    const failedItems = await offlineDatabase.queue
      .where('status')
      .equals('failed')
      .toArray()

    for (const item of failedItems) {
      await offlineDatabase.queue.update(item.id!, {
        status: 'pending',
        error: undefined,
        processedAt: undefined,
      })
    }
  }

  // Clear old completed/failed items (call periodically)
  async cleanupOldItems(maxAge: number = 7 * 24 * 60 * 60 * 1000): Promise<void> {
    const cutoff = Date.now() - maxAge
    
    await offlineDatabase.queue
      .where('processedAt')
      .below(cutoff)
      .delete()

    await offlineDatabase.timeEntries
      .where('updatedAt')
      .below(cutoff)
      .and(item => item.status === 'synced')
      .delete()
  }
}

export const offlineQueueService = new OfflineQueueService()

export default offlineQueueService
